import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const TabunganSummary = ({ onBack, onViewDetail, activeBottomTab, setActiveBottomTab }) => {
  // State untuk data dari API
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState({
    totalTabungan: 0,
    totalNilai: 'Rp 0',
    tabunganAktif: 0,
    tabunganSelesai: 0
  });
  const [tabunganList, setTabunganList] = useState([]);

  // Format currency
  const formatCurrency = (value) => {
    if (typeof value === 'string') {
      return value;
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  // Fetch data tabungan dari API
  useEffect(() => {
    const fetchTabunganData = async () => {
      try {
        setLoading(true);
        
        // Get user_id dari localStorage
        let userId = null;
        const savedUserData = localStorage.getItem('user_data');
        if (savedUserData) {
          const parsed = JSON.parse(savedUserData);
          userId = parsed.id;
        }

        if (!userId) {
          setSummaryData({
            totalTabungan: 0,
            totalNilai: 'Rp 0',
            tabunganAktif: 0,
            tabunganSelesai: 0
          });
          setTabunganList([]);
          setLoading(false);
          return;
        }

        // Fetch tabungan data
        const params = new URLSearchParams({
          user_id: userId
        });

        const response = await apiClient.get(`${API_ENDPOINTS.TABUNGAN.LIST}?${params.toString()}`);

        // Handle response structure
        let tabunganData = [];
        if (response.data && response.data.tabungans && Array.isArray(response.data.tabungans)) {
          tabunganData = response.data.tabungans;
        } else if (response.data && Array.isArray(response.data)) {
          tabunganData = response.data;
        } else if (Array.isArray(response)) {
          tabunganData = response;
        }

        // Filter hanya tabungan aktif (deleted_at === null)
        const tabunganAktif = tabunganData.filter(item => item.deleted_at === null);
        const tabunganSelesai = tabunganData.filter(item => item.deleted_at !== null);

        // Hitung total tabungan
        const totalTabungan = tabunganAktif.length;

        // Fetch produk details dan pembayaran untuk setiap tabungan
        const produkIds = tabunganAktif
          .map(item => item.produk_id)
          .filter(id => id !== null && id !== undefined);

        let produkMap = {};
        let pembayaranMap = {}; // Map tabungan_id ke list pembayaran
        let totalHarga = 0;

        try {
          // Fetch pembayaran untuk setiap tabungan (selalu dilakukan)
          const pembayaranPromises = tabunganAktif.map(async (tabungan) => {
            try {
              const pembayaranResponse = await apiClient.get(API_ENDPOINTS.PEMBAYARAN.BY_TABUNGAN(tabungan.id));
              
              // Handle response structure
              let pembayaranList = [];
              if (pembayaranResponse.data && Array.isArray(pembayaranResponse.data)) {
                pembayaranList = pembayaranResponse.data;
              } else if (pembayaranResponse.data && pembayaranResponse.data.pembayarans && Array.isArray(pembayaranResponse.data.pembayarans)) {
                pembayaranList = pembayaranResponse.data.pembayarans;
              } else if (Array.isArray(pembayaranResponse)) {
                pembayaranList = pembayaranResponse;
              }
              
              return {
                tabunganId: tabungan.id,
                pembayaranList: pembayaranList
              };
            } catch (error) {
              console.error(`Error fetching pembayaran untuk tabungan ${tabungan.id}:`, error);
              return {
                tabunganId: tabungan.id,
                pembayaranList: []
              };
            }
          });

          // Fetch detail produk untuk setiap produk_id (jika ada)
          let produkPromises = [];
          if (produkIds.length > 0) {
            const uniqueProdukIds = [...new Set(produkIds)];
            produkPromises = uniqueProdukIds.map(async (produkId) => {
              try {
                const produkResponse = await apiClient.get(API_ENDPOINTS.PRODUK.DETAIL(produkId));
                
                // Handle response structure
                let produk = null;
                if (produkResponse.data && produkResponse.data.produk) {
                  produk = produkResponse.data.produk;
                } else if (produkResponse.data && !produkResponse.data.produk) {
                  produk = produkResponse.data;
                } else if (!produkResponse.data) {
                  produk = produkResponse;
                }
                
                return {
                  produkId,
                  produkData: produk
                };
              } catch (error) {
                console.error(`Error fetching produk ${produkId}:`, error);
                return {
                  produkId,
                  produkData: null
                };
              }
            });
          }

          // Wait for all promises
          const [pembayaranResults, produkResults] = await Promise.all([
            Promise.all(pembayaranPromises),
            produkPromises.length > 0 ? Promise.all(produkPromises) : Promise.resolve([])
          ]);
          
          // Map produk_id ke produk data
          if (produkResults.length > 0) {
            produkResults.forEach(item => {
              produkMap[item.produkId] = item.produkData;
            });
          }

          // Map tabungan_id ke list pembayaran
          pembayaranResults.forEach(item => {
            pembayaranMap[item.tabunganId] = item.pembayaranList;
          });

          // Hitung total harga
          totalHarga = tabunganAktif.reduce((sum, tabungan) => {
            if (tabungan.produk_id && produkMap[tabungan.produk_id]) {
              const produk = produkMap[tabungan.produk_id];
              const harga = produk.harga_beli || produk.price || produk.harga || 0;
              return sum + (typeof harga === 'number' ? harga : parseFloat(harga) || 0);
            }
            return sum;
          }, 0);
        } catch (error) {
          console.error('Error fetching produk atau pembayaran:', error);
        }

        // Transform data untuk UI
        const transformedList = tabunganAktif.map((tabungan) => {
          const produk = produkMap[tabungan.produk_id] || {};
          const hargaBeli = produk.harga_beli || produk.price || produk.harga || 0;
          const hargaBeliNumber = typeof hargaBeli === 'number' ? hargaBeli : parseFloat(hargaBeli) || 0;
          const namaProduk = produk.nama || produk.name || tabungan.produk_nama || tabungan.deskripsi || 'Produk';
          const kategoriProduk = produk.kategori_produk?.nama || produk.kategori || '';
          
          // Tentukan type dan icon berdasarkan kategori atau default
          let type = 'Tabungan';
          let icon = 'fa-hand-holding-dollar';
          
          if (kategoriProduk.toLowerCase().includes('qurban')) {
            type = 'Tabungan Qurban';
            icon = 'fa-hand-holding-dollar';
          } else if (kategoriProduk.toLowerCase().includes('aqiqah')) {
            type = 'Tabungan Aqiqah';
            icon = 'fa-baby';
          } else if (kategoriProduk.toLowerCase().includes('kurban')) {
            type = 'Tabungan Qurban';
            icon = 'fa-hand-holding-dollar';
          }

          // Hitung total pembayaran dari list pembayaran
          const pembayaranList = pembayaranMap[tabungan.id] || [];
          const totalPembayaran = pembayaranList.reduce((sum, pembayaran) => {
            const nominal = pembayaran.nominal || pembayaran.amount || pembayaran.jumlah || 0;
            const nominalNumber = typeof nominal === 'number' ? nominal : parseFloat(nominal) || 0;
            return sum + nominalNumber;
          }, 0);
          
          // Tentukan status berdasarkan perbandingan total pembayaran dengan harga beli
          // Jika total pembayaran >= harga beli, maka status = Lunas
          const isLunas = hargaBeliNumber > 0 && totalPembayaran >= hargaBeliNumber;
          const status = isLunas ? 'Lunas' : 'Aktif';
          const statusColor = isLunas ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600';
          
          // Hitung progress persentase: (total pembayaran / harga beli) * 100
          // Jika sudah lunas, progress selalu 100%
          const progress = isLunas ? 100 : (hargaBeliNumber > 0 
            ? Math.min(Math.round((totalPembayaran / hargaBeliNumber) * 100), 100)
            : 0);
          
          // Hitung sisa hari (default 45 hari jika tidak ada data)
          const sisaHari = 45; // TODO: bisa dikembangkan dengan hit endpoint atau field tanggal

          return {
            id: tabungan.id,
            type: type,
            icon: icon,
            jumlah: '1 Ekor',
            nilai: formatCurrency(hargaBeliNumber),
            status: status,
            statusColor: statusColor,
            description: namaProduk,
            progress: progress,
            totalPembayaran: totalPembayaran,
            hargaBeli: hargaBeliNumber,
            sisaHari: sisaHari,
            isSold: produk.is_sold || false,
            rawData: tabungan,
            produkData: produk,
            pembayaranList: pembayaranList
          };
        });

        // Pisahkan tabungan aktif dan selesai berdasarkan total pembayaran vs harga beli
        const tabunganAktifList = transformedList.filter(item => item.status === 'Aktif');
        const tabunganSelesaiList = transformedList.filter(item => item.status === 'Lunas');
        
        // Hitung total harga hanya dari tabungan aktif
        const totalHargaAktif = tabunganAktifList.reduce((sum, item) => {
          return sum + (item.hargaBeli || 0);
        }, 0);

        // Update summary data
        setSummaryData({
          totalTabungan: totalTabungan,
          totalNilai: formatCurrency(totalHargaAktif),
          tabunganAktif: tabunganAktifList.length,
          tabunganSelesai: tabunganSelesaiList.length
        });

        setTabunganList(transformedList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tabungan data:', error);
        setSummaryData({
          totalTabungan: 0,
          totalNilai: 'Rp 0',
          tabunganAktif: 0,
          tabunganSelesai: 0
        });
        setTabunganList([]);
        setLoading(false);
      }
    };

    fetchTabunganData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Wrapper */}
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button 
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
              </button>
              <div className="flex items-center space-x-2">
                <Logo size="small" showText={false} />
                <span className="font-bold text-primary text-lg">Tabungan Saya</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-32 bg-gray-50">
          {/* Summary Cards */}
          <section className="px-4 pt-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                <div className="bg-primary p-3 rounded-lg w-fit mx-auto mb-3">
                  <i className="fa-solid fa-piggy-bank text-accent text-2xl"></i>
                </div>
                <h4 className="text-gray-600 text-xs font-medium">Total Tabungan</h4>
                <p className="text-2xl font-bold text-primary mt-1">{summaryData.totalTabungan}</p>
                <p className="text-xs text-gray-500">Jenis Tabungan</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                <div className="bg-secondary p-3 rounded-lg w-fit mx-auto mb-3">
                  <i className="fa-solid fa-wallet text-accent text-2xl"></i>
                </div>
                <h4 className="text-gray-600 text-xs font-medium">Total Nilai</h4>
                <p className="text-2xl font-bold text-primary mt-1">{summaryData.totalNilai}</p>
                <p className="text-xs text-gray-500">Semua Tabungan</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                <div className="bg-green-500 p-3 rounded-lg w-fit mx-auto mb-3">
                  <i className="fa-solid fa-check-circle text-white text-2xl"></i>
                </div>
                <h4 className="text-gray-600 text-xs font-medium">Tabungan Aktif</h4>
                <p className="text-2xl font-bold text-green-600 mt-1">{summaryData.tabunganAktif}</p>
                <p className="text-xs text-gray-500">Sedang Berjalan</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                <div className="bg-blue-500 p-3 rounded-lg w-fit mx-auto mb-3">
                  <i className="fa-solid fa-trophy text-white text-2xl"></i>
                </div>
                <h4 className="text-gray-600 text-xs font-medium">Tabungan Selesai</h4>
                <p className="text-2xl font-bold text-blue-600 mt-1">{summaryData.tabunganSelesai}</p>
                <p className="text-xs text-gray-500">Telah Lunas</p>
              </div>
            </div>
          </section>

          {/* Daftar Tabungan */}
          <section className="px-4 mb-6">
            <h3 className="text-gray-700 font-semibold mb-4">Daftar Tabungan</h3>
            
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="bg-gray-200 p-3 rounded-xl w-12 h-12"></div>
                          <div>
                            <div className="bg-gray-200 h-5 w-32 rounded mb-2"></div>
                            <div className="bg-gray-200 h-4 w-24 rounded"></div>
                          </div>
                        </div>
                        <div className="bg-gray-200 h-6 w-16 rounded-full"></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="bg-gray-200 h-3 w-12 rounded mb-2"></div>
                          <div className="bg-gray-200 h-5 w-20 rounded"></div>
                        </div>
                        <div>
                          <div className="bg-gray-200 h-3 w-16 rounded mb-2"></div>
                          <div className="bg-gray-200 h-5 w-24 rounded"></div>
                        </div>
                      </div>
                      <div className="mb-4">
                        <div className="bg-gray-200 h-2 w-full rounded-full"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : tabunganList.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <i className="fa-solid fa-piggy-bank text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Belum ada tabungan</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tabunganList.map((tabungan) => (
                <div key={tabungan.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-secondary p-3 rounded-xl">
                          <i className={`fa-solid ${tabungan.icon} text-accent text-xl`}></i>
                        </div>
                        <div>
                          <h4 className="text-primary font-bold text-lg">{tabungan.type}</h4>
                          <p className="text-gray-600 text-sm">{tabungan.description}</p>
                        </div>
                      </div>
                      <span className={`text-xs ${tabungan.statusColor} font-semibold px-3 py-1 rounded-full`}>
                        {tabungan.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-500 text-xs">Jumlah</p>
                        <p className="text-primary font-bold">{tabungan.jumlah}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Total Nilai</p>
                        <p className="text-primary font-bold">{tabungan.nilai}</p>
                      </div>
                    </div>

                    {/* Status Terjual */}
                    {tabungan.isSold && (
                      <div className="mb-4">
                        <div className="bg-red-100 text-red-600 px-3 py-2 rounded-lg flex items-center space-x-2">
                          <i className="fa-solid fa-check-circle"></i>
                          <span className="text-sm font-semibold">Produk Terjual</span>
                        </div>
                      </div>
                    )}

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-500">Progress Tabungan</span>
                        <span className="text-xs font-semibold text-primary">{tabungan.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${tabungan.status === 'Aktif' ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{ width: `${tabungan.progress}%` }}
                        ></div>
                      </div>
                      {tabungan.sisaHari > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{tabungan.sisaHari} hari lagi</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <div className="px-4 pb-4">
                    <button 
                      onClick={() => onViewDetail(tabungan)}
                      className="w-full bg-accent text-primary font-semibold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 hover:bg-accent/90 transition-colors"
                    >
                      <i className="fa-solid fa-eye"></i>
                      <span>Lihat Detail</span>
                    </button>
                  </div>
                </div>
              ))}
              </div>
            )}
          </section>
          
          {/* Extra spacing for bottom navigation */}
          <div className="h-8"></div>
        </main>

        {/* Bottom Navigation */}
        <footer className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white pt-2">
          <div className="bg-primary mx-4 mb-4 rounded-2xl shadow-lg">
            <div className="flex justify-around items-center h-20 text-white">
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'home' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('home');
                  onBack();
                }}
              >
                <i className="fa-solid fa-house text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Beranda</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'savings' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('savings')}
              >
                <i className="fa-solid fa-piggy-bank text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Tabungan</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'products' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('products');
                  onBack();
                }}
              >
                <i className="fa-solid fa-box-archive text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Produk</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'investment' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('investment');
                  onBack();
                }}
              >
                <i className="fa-solid fa-arrow-trend-up text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Investasi</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'account' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('account');
                  onBack();
                }}
              >
                <i className="fa-solid fa-user-circle text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Akun</span>
              </span>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default TabunganSummary;
